// API functions for controls and control sub-resources.
// Controls use a flat route structure (not nested under categories).
// Permission filtering is done server-side via control category grants.

import type { ApiClient } from './client'

// --- Types ---

/** Control list response (summary fields). */
export interface Control {
  id: string
  name: string
  description?: string | null
  lifecycle_state: 'draft' | 'active' | 'retired'
  category_id: string
  category_name: string
  owner_role_id: string
  owner_user_id?: string | null
  owner_display_name?: string | null
  short_code: string
  scope_value?: string | null
  scope_coverage_percent?: number | null
  linked_risk_count: number
  linked_action_count: number
  compliance_mapping_count: number
  has_overdue_assessments: boolean
  created_at: string
  updated_at: string
}

/** Control detail response (extends list with additional fields). */
export interface ControlDetail extends Control {
  control_owner?: string | null
  control_activity?: string | null
  control_location?: string | null
  control_trigger?: string | null
  control_rationale?: string | null
  scope_exceptions?: string | null
  scope_updated_at?: string | null
  scope_updated_by?: string | null
  created_via: string
  created_by: string
}

export interface CreateControlRequest {
  name: string
  description?: string
  category_id: string
  owner_role_id: string
  control_owner?: string
  control_activity?: string
  control_location?: string
  control_trigger?: string
  control_rationale?: string
  scope_value?: string
  scope_exceptions?: string
}

export interface UpdateControlRequest {
  name?: string
  description?: string
  control_owner?: string
  control_activity?: string
  control_location?: string
  control_trigger?: string
  control_rationale?: string
  scope_value?: string
  scope_coverage_percent?: number
  scope_exceptions?: string
  change_notes?: string
}

export interface RetireControlRequest {
  change_notes?: string
}

// --- Control history types ---

export interface ControlHistoryEntry {
  id: string
  control_id: string
  changed_at: string
  changed_by: string
  changed_by_name: string
  change_notes?: string | null
  lifecycle_state: string
  scope_value?: string | null
  scope_coverage_percent?: number | null
  scope_exceptions?: string | null
}

// --- Cross-reference types ---

/** Risk linked to a control via control_risk_links (read from control side). */
export interface LinkedRiskSummary {
  id: string
  name: string
  lifecycle_state: string
  register_id: string
  register_name: string
  owner_display_name?: string | null
  linked_at: string
  linked_by: string
  linked_by_name: string
}

/** Compliance framework mapping for a control. */
export interface ComplianceMappingSummary {
  id: string
  framework_id: string
  framework_name: string
  framework_item_id: string
  reference_code: string
  title: string
  requirement_group: string
  tenant_interpretation?: string | null
  coverage_level: string
  coverage_notes?: string | null
  mapped_at: string
  mapped_by: string
  mapped_by_name: string
}

// --- Evidence types ---

/** Evidence attached to a control. */
export interface ControlEvidence {
  id: string
  file_id: string
  file_name: string
  file_size_bytes: number
  mime_type: string
  download_url: string
  evidence_tag_id: string
  tag_name: string
  comment?: string | null
  is_archived: boolean
  archived_by?: string | null
  archived_at?: string | null
  uploaded_by: string
  uploaded_by_name: string
  uploaded_at: string
}

// --- Assessment types ---

export interface ControlAssessment {
  id: string
  control_id: string
  assessment_type: string
  effectiveness_state: string
  assessed_date?: string | null
  assessed_by?: string | null
  assessed_by_name?: string
  comment?: string | null
  is_overdue: boolean
  days_since_assessed?: number | null
  threshold_days?: number | null
}

export interface RecordAssessmentRequest {
  assessment_type: string
  effectiveness_state: string
  assessed_date: string // YYYY-MM-DD
  comment?: string
}

/** Assessment history entry from the assessment-specific audit log. */
export interface AssessmentHistoryEntry {
  id: string
  control_id: string
  changed_at: string
  assessment_type: string
  effectiveness_state: string
  assessed_date?: string | null
  assessed_by?: string | null
  assessed_by_name?: string
  comment?: string | null
  change_notes?: string | null
}

// --- List filters ---

export interface ControlListFilter {
  lifecycle_state?: string
  category_id?: string
  search?: string
}

// --- Lifecycle state helpers ---

export const controlLifecycleStates = ['draft', 'active', 'retired'] as const

export const controlLifecycleStateLabels: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  retired: 'Retired',
}

export const controlLifecycleStateColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  retired: 'bg-slate-100 text-slate-500',
}

// --- API functions ---

function buildFilterParams(filter?: ControlListFilter): string {
  if (!filter) return ''
  const params = new URLSearchParams()
  if (filter.lifecycle_state) params.set('lifecycle_state', filter.lifecycle_state)
  if (filter.category_id) params.set('category_id', filter.category_id)
  if (filter.search) params.set('search', filter.search)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export function controlsApi(api: ApiClient) {
  return {
    // Control CRUD (flat routes, permission-filtered by category)
    list: (filter?: ControlListFilter) =>
      api.get<Control[]>(`/controls${buildFilterParams(filter)}`),
    create: (data: CreateControlRequest) => api.post<ControlDetail>('/controls', data),
    get: (controlId: string) => api.get<ControlDetail>(`/controls/${controlId}`),
    update: (controlId: string, data: UpdateControlRequest) =>
      api.put<ControlDetail>(`/controls/${controlId}`, data),

    // State transitions
    activate: (controlId: string) =>
      api.post<ControlDetail>(`/controls/${controlId}/activate`),
    retire: (controlId: string, data?: RetireControlRequest) =>
      api.post<ControlDetail>(`/controls/${controlId}/retire`, data),

    // Assessments
    assessments: (controlId: string) =>
      api.get<ControlAssessment[]>(`/controls/${controlId}/assessments`),
    recordAssessment: (controlId: string, data: RecordAssessmentRequest) =>
      api.post<ControlAssessment>(`/controls/${controlId}/assessments`, data),

    // Assessment history (last 365 days)
    assessmentHistory: (controlId: string) =>
      api.get<AssessmentHistoryEntry[]>(`/controls/${controlId}/assessment-history`),

    // Audit history
    history: (controlId: string) =>
      api.get<ControlHistoryEntry[]>(`/controls/${controlId}/history`),

    // Evidence
    evidence: (controlId: string, includeArchived?: boolean) =>
      api.get<ControlEvidence[]>(
        `/controls/${controlId}/evidence${includeArchived ? '?include_archived=true' : ''}`,
      ),
    uploadEvidence: async (controlId: string, params: { file: File; evidenceTagId: string; comment?: string }) => {
      const formData = new FormData()
      formData.append('file', params.file)
      formData.append('evidence_tag_id', params.evidenceTagId)
      if (params.comment) formData.append('comment', params.comment)
      const response = await api.fetch(`/controls/${controlId}/evidence`, {
        method: 'POST',
        body: formData,
      })
      return response.json() as Promise<ControlEvidence>
    },
    archiveEvidence: async (controlId: string, evidenceId: string) => {
      const response = await api.fetch(`/controls/${controlId}/evidence/${evidenceId}`, {
        method: 'PATCH',
      })
      return response.json() as Promise<{ status: string }>
    },

    // Cross-references
    risks: (controlId: string) =>
      api.get<LinkedRiskSummary[]>(`/controls/${controlId}/risks`),
    frameworks: (controlId: string) =>
      api.get<ComplianceMappingSummary[]>(`/controls/${controlId}/frameworks`),

    // Move to a different category
    move: (controlId: string, destCategoryId: string, changeNotes?: string) =>
      api.post<ControlDetail>(`/controls/${controlId}/move`, {
        dest_category_id: destCategoryId,
        change_notes: changeNotes ?? '',
      }),
  }
}
